<script setup lang="ts">
import { computed, ref } from "vue";
import { api } from "../api";
import type { AgentWeekInput } from "../shared";

const props = defineProps<{ initial: AgentWeekInput; draftId?: string }>();
const emit = defineEmits<{ cancel: []; saved: []; published: []; error: [message: string] }>();
const week = ref<AgentWeekInput>(JSON.parse(JSON.stringify(props.initial)) as AgentWeekInput);
const currentDraftId = ref(props.draftId);
const saving = ref(false);
const warmupText = computed({
  get: () => (week.value.warmup ?? []).join("\n"),
  set: (value: string) => { week.value.warmup = value.split("\n").map((item) => item.trim()).filter(Boolean); }
});

function isoDate(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function addDay() {
  const date = new Date(`${week.value.week_start}T12:00:00`);
  date.setDate(date.getDate() + week.value.days.length * 2);
  week.value.days.push({ day_date: isoDate(date), title: `Workout ${week.value.days.length + 1}`, estimated_minutes: 50, blocks: [] });
}

function addBlock(dayIndex: number) {
  const blocks = week.value.days[dayIndex].blocks;
  blocks.push({
    label: String.fromCharCode(65 + blocks.length), kind: "superset", rounds: 3, rest_seconds: 90,
    exercises: [
      { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 },
      { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 }
    ]
  });
}

function addExercise(dayIndex: number, blockIndex: number) {
  const block = week.value.days[dayIndex].blocks[blockIndex];
  block.exercises.push({ name: "", target_sets: block.rounds, rep_min: 8, rep_max: 10, target_rir: 2 });
}

async function save(): Promise<string | undefined> {
  saving.value = true;
  try {
    if (currentDraftId.value) await api.updateDraft(currentDraftId.value, week.value);
    else currentDraftId.value = (await api.createDraft(week.value)).draft.id;
    emit("saved");
    return currentDraftId.value;
  } catch (caught) {
    emit("error", caught instanceof Error ? caught.message : "Could not save draft");
  } finally { saving.value = false; }
}

async function publish() {
  const draftId = await save();
  if (!draftId) return;
  saving.value = true;
  try {
    await api.publishDraft(draftId);
    emit("published");
  } catch (caught) {
    emit("error", caught instanceof Error ? caught.message : "Could not publish week");
  } finally { saving.value = false; }
}
</script>

<template>
  <section class="plan-builder">
    <header class="builder-header">
      <div><p class="eyebrow">Manual programming</p><h1>Build a week</h1><p>Draft freely, then publish the whole week when it is ready.</p></div>
      <div class="builder-actions"><button class="button ghost" @click="emit('cancel')">Close</button><button class="button secondary" :disabled="saving" @click="save">Save draft</button><button class="button primary" :disabled="saving" @click="publish">Publish week</button></div>
    </header>

    <section class="builder-card builder-week-fields">
      <label>Week starts<input v-model="week.week_start" type="date"></label>
      <label>Week title<input v-model="week.title" placeholder="Strength + capacity"></label>
      <label>Focus<input v-model="week.focus" placeholder="What should improve this week?"></label>
      <label class="wide">Warm-up checklist<textarea v-model="warmupText" rows="4" placeholder="One preparation step per line"></textarea></label>
      <label class="wide">Coach notes<textarea v-model="week.coach_notes" rows="3" placeholder="Progression rules, recovery notes, or intent"></textarea></label>
    </section>

    <article v-for="(day, dayIndex) in week.days" :key="dayIndex" class="builder-day">
      <header>
        <span>{{ String(dayIndex + 1).padStart(2, "0") }}</span>
        <div><p class="eyebrow">Workout day</p><h2>{{ day.title || "Untitled workout" }}</h2></div>
        <button class="text-button danger" :disabled="week.days.length === 1" @click="week.days.splice(dayIndex, 1)">Remove day</button>
      </header>
      <div class="builder-day-fields">
        <label>Date<input v-model="day.day_date" type="date"></label>
        <label>Title<input v-model="day.title" placeholder="Upper A"></label>
        <label>Minutes<input v-model.number="day.estimated_minutes" type="number" min="5" max="300" inputmode="numeric"></label>
        <label class="wide">Focus<input v-model="day.focus" placeholder="Heavy press, upper-back volume…"></label>
      </div>

      <section v-for="(block, blockIndex) in day.blocks" :key="blockIndex" class="builder-block">
        <header>
          <b>{{ block.label || String.fromCharCode(65 + blockIndex) }}</b><span>{{ block.kind }}</span>
          <button class="text-button danger" @click="day.blocks.splice(blockIndex, 1)">Remove block</button>
        </header>
        <div class="builder-block-fields">
          <label>Label<input v-model="block.label" maxlength="20"></label>
          <label>Format<select v-model="block.kind"><option value="superset">Superset</option><option value="straight">Straight sets</option><option value="circuit">Circuit</option></select></label>
          <label>Rounds<input v-model.number="block.rounds" type="number" min="1" max="12" inputmode="numeric"></label>
          <label>Rest seconds<input v-model.number="block.rest_seconds" type="number" min="0" max="900" inputmode="numeric"></label>
          <label class="wide">Block notes<input v-model="block.notes" placeholder="Transition or tempo guidance"></label>
        </div>
        <div class="builder-exercises">
          <article v-for="(exercise, exerciseIndex) in block.exercises" :key="exerciseIndex" class="builder-exercise">
            <header><b>{{ block.label || String.fromCharCode(65 + blockIndex) }}{{ exerciseIndex + 1 }}</b><button class="text-button danger" :disabled="block.exercises.length === 1" @click="block.exercises.splice(exerciseIndex, 1)">Remove</button></header>
            <label>Exercise<input v-model="exercise.name" placeholder="Exercise name"></label>
            <label>Equipment<input v-model="exercise.equipment" placeholder="Cable, dumbbell…"></label>
            <div class="builder-targets">
              <label>Sets<input v-model.number="exercise.target_sets" type="number" min="1" max="12" inputmode="numeric"></label>
              <label>Rep min<input v-model.number="exercise.rep_min" type="number" min="1" inputmode="numeric"></label>
              <label>Rep max<input v-model.number="exercise.rep_max" type="number" min="1" inputmode="numeric"></label>
              <label>Seconds<input v-model.number="exercise.duration_seconds" type="number" min="1" max="3600" inputmode="numeric" placeholder="Instead of reps"></label>
              <label>RIR<input v-model.number="exercise.target_rir" type="number" min="0" max="10" step="0.5" inputmode="decimal"></label>
            </div>
            <label>Target weight<input v-model.number="exercise.target_weight" type="number" min="0" step="0.5" inputmode="decimal" placeholder="Optional"></label>
            <label>Technique video<input v-model="exercise.video_url" type="url" placeholder="https://…"></label>
            <label>Guidance<textarea v-model="exercise.notes" rows="2" placeholder="Cue + progression rule"></textarea></label>
          </article>
        </div>
        <button class="add-row" @click="addExercise(dayIndex, blockIndex)">＋ Add exercise</button>
      </section>
      <button class="add-row" @click="addBlock(dayIndex)">＋ Add block</button>
    </article>
    <button class="button ghost full" @click="addDay">＋ Add workout day</button>
    <div class="builder-actions builder-actions-bottom"><button class="button secondary" :disabled="saving" @click="save">Save draft</button><button class="button primary" :disabled="saving" @click="publish">Publish week</button></div>
  </section>
</template>
